import React, { useState, useEffect } from 'react';
import { Upload, X, Loader2, Trash2, Edit2, Save, LogOut } from 'lucide-react';
import { adminApi as axios } from '../lib/adminApi';
import { useToast } from '../hooks/use-toast';
import '../styles/admin.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = ({ onClose, onUploadSuccess, onLogout }) => {
  const [activeTab, setActiveTab] = useState('videos');
  const [uploading, setUploading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const { toast } = useToast();

  // Settings state
  const [heroSettings, setHeroSettings] = useState({
    background_type: 'image',
    background_url: '',
    title: 'Pedro Mota Visuals',
    subtitle: 'Video & Photography'
  });
  
  const [profileSettings, setProfileSettings] = useState({
    name: 'Pedro Mota',
    role: 'Audiovisual Director & Cinematographer',
    description: '',
    email: 'motapedro001@gmail.com',
    phone: '+351 966 656 362',
    instagram: '@pedromota.visuals',
    vimeo: 'pedromota',
    linkedin: 'pedro-mota-visuals',
    profile_image_url: null
  });
  const [videoData, setVideoData] = useState({
    title: '',
    category: '',
    duration: '',
    videoFile: null,
    thumbnailFile: null
  });

  useEffect(() => {
    if (activeTab === 'videos') {
      fetchVideos();
    } else if (activeTab === 'settings') {
      fetchSettings();
    }
  }, [activeTab]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setHeroSettings(response.data.hero);
      setProfileSettings(response.data.profile);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const handleVideoUpload = async (e) => {
    e.preventDefault();
    
    if (!videoData.videoFile) {
      toast({ title: 'Erro', description: 'Selecione um arquivo de vídeo' });
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('title', videoData.title);
      formData.append('category', videoData.category);
      formData.append('duration', videoData.duration || '0:00');
      formData.append('video_file', videoData.videoFile);
      if (videoData.thumbnailFile) {
        formData.append('thumbnail_file', videoData.thumbnailFile);
      }

      await axios.post(`${BACKEND_URL}/api/videos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({ title: 'Sucesso!', description: 'Vídeo enviado com sucesso' });
      
      setVideoData({
        title: '',
        category: '',
        duration: '',
        videoFile: null,
        thumbnailFile: null
      });
      
      fetchVideos();
      if (onUploadSuccess) onUploadSuccess();
      
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Erro', description: 'Falha ao enviar vídeo' });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Tem certeza que deseja apagar este vídeo?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/videos/${videoId}`);
      toast({ title: 'Sucesso!', description: 'Vídeo apagado' });
      fetchVideos();
      if (onUploadSuccess) onUploadSuccess();
    } catch (error) {
      toast({ title: 'Erro', description: 'Falha ao apagar vídeo' });
    }
  };

  const handleMoveVideo = async (index, direction) => {
    const newVideos = [...videos];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newVideos.length) return;
    
    // Swap videos
    [newVideos[index], newVideos[targetIndex]] = [newVideos[targetIndex], newVideos[index]];
    
    // Update local state immediately for better UX
    setVideos(newVideos);
    
    // Send new order to backend
    try {
      const videoIds = newVideos.map(v => v.id);
      await axios.put(`${BACKEND_URL}/api/videos/reorder`, videoIds);
      toast({ title: 'Sucesso!', description: 'Ordem atualizada' });
      if (onUploadSuccess) onUploadSuccess();
    } catch (error) {
      toast({ title: 'Erro', description: 'Falha ao reordenar' });
      // Revert on error
      fetchVideos();
    }
  };

  const handleEditVideo = (video) => {
    setEditingItem({ ...video, type: 'video' });
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;

    try {
      if (editingItem.type === 'video') {
        // Update video metadata
        await axios.put(`${BACKEND_URL}/api/videos/${editingItem.id}`, {
          title: editingItem.title,
          category: editingItem.category,
          duration: editingItem.duration
        });
        
        // Update thumbnail if new file selected
        if (editingItem.newThumbnail) {
          const formData = new FormData();
          formData.append('thumbnail_file', editingItem.newThumbnail);
          
          await axios.put(
            `${BACKEND_URL}/api/videos/${editingItem.id}/thumbnail`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
        }
        
        toast({ title: 'Sucesso!', description: 'Vídeo atualizado' });
        fetchVideos();
      }
      
      setEditingItem(null);
      if (onUploadSuccess) onUploadSuccess();
    } catch (error) {
      console.error('Update error:', error);
      toast({ title: 'Erro', description: 'Falha ao atualizar' });
    }
  };

  return (
    <div className="admin-overlay" data-testid="admin-overlay">
      <div className="admin-panel" data-testid="admin-panel" role="dialog" aria-label="Painel de Administração">
        <div className="admin-header">
          <h2>Painel de Administração</h2>
          <div style={{display: 'flex', gap: '12px', flexShrink: 0}}>
            <button data-testid="admin-logout-button" className="admin-close" onClick={onLogout} title="Terminar sessão" aria-label="Terminar sessão">
              <LogOut size={22} />
            </button>
            <button data-testid="admin-close-button" className="admin-close" onClick={onClose} aria-label="Fechar administração">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="admin-tabs">
          <button 
            data-testid="admin-videos-tab"
            className={`admin-tab ${activeTab === 'videos' ? 'active' : ''}`}
            onClick={() => setActiveTab('videos')}
          >
            Vídeos
          </button>
          <button 
            data-testid="admin-settings-tab"
            className={`admin-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Configurações
          </button>
        </div>

        <div className="admin-content">
          {activeTab === 'videos' && (
            <>
              <form onSubmit={handleVideoUpload} className="upload-form">
                <h3 className="section-title-small">Adicionar Novo Vídeo</h3>
                <div className="form-group">
                  <label>Título</label>
                  <input
                    type="text"
                    data-testid="video-upload-title-input"
                    value={videoData.title}
                    onChange={(e) => setVideoData({...videoData, title: e.target.value})}
                    required
                    placeholder="Ex: Corporate Brand Film"
                  />
                </div>

                <div className="form-group">
                  <label>Categoria</label>
                  <input
                    type="text"
                    data-testid="video-upload-category-input"
                    value={videoData.category}
                    onChange={(e) => setVideoData({...videoData, category: e.target.value})}
                    required
                    placeholder="Ex: Commercial, Wedding, Documentary"
                  />
                </div>

                <div className="form-group">
                  <label>Duração</label>
                  <input
                    type="text"
                    data-testid="video-upload-duration-input"
                    value={videoData.duration}
                    onChange={(e) => setVideoData({...videoData, duration: e.target.value})}
                    placeholder="Ex: 2:30"
                  />
                </div>

                <div className="form-group">
                  <label>Arquivo de Vídeo</label>
                  <input
                    type="file"
                    data-testid="video-upload-file-input"
                    accept="video/*"
                    onChange={(e) => setVideoData({...videoData, videoFile: e.target.files[0]})}
                    required
                  />
                  {videoData.videoFile && (
                    <p className="file-name" data-testid="video-upload-selected-file">{videoData.videoFile.name}</p>
                  )}
                </div>

                <div className="form-group">
                  <label>Thumbnail (Opcional)</label>
                  <input
                    type="file"
                    data-testid="video-upload-thumbnail-input"
                    accept="image/*"
                    onChange={(e) => setVideoData({...videoData, thumbnailFile: e.target.files[0]})}
                  />
                  {videoData.thumbnailFile && (
                    <p className="file-name" data-testid="video-upload-selected-thumbnail">{videoData.thumbnailFile.name}</p>
                  )}
                </div>

                <button data-testid="video-upload-submit-button" type="submit" className="upload-button" disabled={uploading}>
                  {uploading ? (
                    <>
                      <Loader2 className="spinner" size={20} />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Upload size={20} />
                      Enviar Vídeo
                    </>
                  )}
                </button>
              </form>

              {videos.length > 0 && (
                <div className="content-list">
                  <h3 className="section-title-small">Vídeos Existentes</h3>
                  {videos.map((video, index) => (
                    <div key={video.id} className="content-item" data-testid={`admin-video-${video.id}`}>
                      <div className="reorder-buttons">
                        <button 
                          data-testid={`video-move-up-${video.id}`}
                          onClick={() => handleMoveVideo(index, 'up')}
                          disabled={index === 0}
                          className="btn-reorder"
                          title="Mover para cima"
                        >
                          ↑
                        </button>
                        <button 
                          data-testid={`video-move-down-${video.id}`}
                          onClick={() => handleMoveVideo(index, 'down')}
                          disabled={index === videos.length - 1}
                          className="btn-reorder"
                          title="Mover para baixo"
                        >
                          ↓
                        </button>
                      </div>
                      <img 
                        src={`${BACKEND_URL}${video.thumbnail_url}`} 
                        alt={video.title}
                        className="content-thumbnail"
                      />
                      <div className="content-info">
                        <h4 data-testid={`admin-video-title-${video.id}`}>{video.title}</h4>
                        <p data-testid={`admin-video-meta-${video.id}`}>{video.category} • {video.duration}</p>
                      </div>
                      <div className="content-actions">
                        <button 
                          data-testid={`video-edit-${video.id}`}
                          onClick={() => handleEditVideo(video)}
                          className="btn-edit"
                          title="Editar"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button 
                          data-testid={`video-delete-${video.id}`}
                          onClick={() => handleDeleteVideo(video.id)}
                          className="btn-delete"
                          title="Apagar"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'settings' && (
            <div className="settings-section">
              <h3 className="section-title-small">Hero (Página Principal)</h3>
              
              <div className="settings-card">
                <h4>Imagem/Vídeo de Fundo</h4>
                <p className="settings-description">Altere a imagem ou vídeo de fundo da página principal</p>
                
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setUploading(true);
                  try {
                    const formData = new FormData();
                    formData.append('background_type', e.target.background_type.value);
                    formData.append('file', e.target.file.files[0]);
                    
                    await axios.put(`${BACKEND_URL}/api/settings/hero/background`, formData, {
                      headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    toast({ title: 'Sucesso!', description: 'Fundo atualizado' });
                    fetchSettings();
                    if (onUploadSuccess) onUploadSuccess();
                  } catch (error) {
                    console.error('Error:', error);
                    toast({ title: 'Erro', description: 'Falha ao atualizar fundo' });
                  } finally {
                    setUploading(false);
                  }
                }}>
                  <div className="form-group">
                    <label>Tipo</label>
                    <select data-testid="hero-background-type-select" name="background_type" className="form-select">
                      <option value="image">Imagem</option>
                      <option value="video">Vídeo</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Arquivo</label>
                    <input data-testid="hero-background-file-input" type="file" name="file" accept="image/*,video/*" required />
                  </div>
                  <button data-testid="hero-background-submit-button" type="submit" className="upload-button" disabled={uploading}>
                    {uploading ? 'Enviando...' : 'Atualizar Fundo'}
                  </button>
                </form>
              </div>

              <div className="settings-card">
                <h4>Textos do Hero</h4>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setUploading(true);
                  try {
                    const formData = new FormData();
                    formData.append('title', e.target.title.value);
                    formData.append('subtitle', e.target.subtitle.value);
                    
                    await axios.put(`${BACKEND_URL}/api/settings/hero/text`, formData);
                    toast({ title: 'Sucesso!', description: 'Textos atualizados' });
                    fetchSettings();
                    if (onUploadSuccess) onUploadSuccess();
                  } catch (error) {
                    console.error('Error:', error);
                    toast({ title: 'Erro', description: 'Falha ao atualizar textos' });
                  } finally {
                    setUploading(false);
                  }
                }}>
                  <div className="form-group">
                    <label>Título Principal</label>
                    <input data-testid="hero-title-input" type="text" name="title" defaultValue={heroSettings.title} required />
                  </div>
                  <div className="form-group">
                    <label>Subtítulo</label>
                    <input data-testid="hero-subtitle-input" type="text" name="subtitle" defaultValue={heroSettings.subtitle} required />
                  </div>
                  <button data-testid="hero-text-submit-button" type="submit" className="upload-button" disabled={uploading}>
                    {uploading ? 'Salvando...' : 'Salvar Textos'}
                  </button>
                </form>
              </div>

              <h3 className="section-title-small" style={{marginTop: '3rem'}}>Perfil Completo</h3>
              
              <div className="settings-card">
                <h4>Informações Pessoais</h4>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setUploading(true);
                  try {
                    const formData = new FormData();
                    
                    // Add text fields
                    formData.append('name', e.target.name.value);
                    formData.append('role', e.target.role.value);
                    formData.append('description', e.target.description.value);
                    formData.append('email', e.target.email.value);
                    formData.append('phone', e.target.phone.value);
                    formData.append('instagram', e.target.instagram.value);
                    formData.append('vimeo', e.target.vimeo.value);
                    formData.append('linkedin', e.target.linkedin.value);
                    
                    // Add file if selected
                    if (e.target.profile_image.files[0]) {
                      formData.append('profile_image', e.target.profile_image.files[0]);
                    }
                    
                    await axios.put(`${BACKEND_URL}/api/settings/profile`, formData, {
                      headers: {
                        'Content-Type': 'multipart/form-data'
                      }
                    });
                    toast({ title: 'Sucesso!', description: 'Perfil atualizado' });
                    fetchSettings();
                    if (onUploadSuccess) onUploadSuccess();
                  } catch (error) {
                    console.error('Error:', error);
                    toast({ title: 'Erro', description: 'Falha ao atualizar perfil' });
                  } finally {
                    setUploading(false);
                  }
                }}>
                  <div className="form-group">
                    <label>Foto de Perfil</label>
                    <input data-testid="profile-image-input" type="file" name="profile_image" accept="image/*" />
                  </div>
                  <div className="form-group">
                    <label>Nome</label>
                    <input data-testid="profile-name-input" type="text" name="name" defaultValue={profileSettings.name} required />
                  </div>
                  <div className="form-group">
                    <label>Função/Cargo</label>
                    <input data-testid="profile-role-input" type="text" name="role" defaultValue={profileSettings.role} required />
                  </div>
                  <div className="form-group">
                    <label>Descrição</label>
                    <textarea data-testid="profile-description-input" name="description" rows={6} defaultValue={profileSettings.description} required></textarea>
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input data-testid="profile-email-input" type="email" name="email" defaultValue={profileSettings.email} required />
                  </div>
                  <div className="form-group">
                    <label>Telemóvel</label>
                    <input data-testid="profile-phone-input" type="tel" name="phone" defaultValue={profileSettings.phone} required />
                  </div>
                  <div className="form-group">
                    <label>Instagram</label>
                    <input data-testid="profile-instagram-input" type="text" name="instagram" defaultValue={profileSettings.instagram} />
                  </div>
                  <div className="form-group">
                    <label>Vimeo</label>
                    <input data-testid="profile-vimeo-input" type="text" name="vimeo" defaultValue={profileSettings.vimeo} />
                  </div>
                  <div className="form-group">
                    <label>LinkedIn</label>
                    <input data-testid="profile-linkedin-input" type="text" name="linkedin" defaultValue={profileSettings.linkedin} />
                  </div>
                  <button data-testid="profile-submit-button" type="submit" className="upload-button" disabled={uploading}>
                    {uploading ? 'Salvando...' : 'Salvar Perfil Completo'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingItem && (
        <div data-testid="video-edit-overlay" className="edit-modal-overlay" onClick={() => setEditingItem(null)}>
          <div data-testid="video-edit-modal" className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Editar {editingItem.type === 'video' ? 'Vídeo' : 'Foto'}</h3>
            
            {editingItem.type === 'video' && (
              <div className="thumbnail-preview">
                <img 
                  src={`${BACKEND_URL}${editingItem.thumbnail_url}`}
                  alt="Thumbnail atual"
                  style={{width: '100%', maxHeight: '200px', objectFit: 'cover', marginBottom: '1rem', borderRadius: '2px'}}
                />
                <div className="form-group">
                  <label>Substituir Thumbnail</label>
                  <input
                    type="file"
                    data-testid="video-edit-thumbnail-input"
                    accept="image/*"
                    onChange={(e) => setEditingItem({...editingItem, newThumbnail: e.target.files[0]})}
                  />
                  {editingItem.newThumbnail && (
                    <p className="file-name" data-testid="video-edit-selected-thumbnail">Nova: {editingItem.newThumbnail.name}</p>
                  )}
                </div>
              </div>
            )}
            
            <div className="form-group">
              <label>Título</label>
              <input
                type="text"
                data-testid="video-edit-title-input"
                value={editingItem.title}
                onChange={(e) => setEditingItem({...editingItem, title: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Categoria</label>
              <input
                type="text"
                data-testid="video-edit-category-input"
                value={editingItem.category}
                onChange={(e) => setEditingItem({...editingItem, category: e.target.value})}
              />
            </div>
            {editingItem.type === 'video' && (
              <div className="form-group">
                <label>Duração</label>
                <input
                  type="text"
                  data-testid="video-edit-duration-input"
                  value={editingItem.duration}
                  onChange={(e) => setEditingItem({...editingItem, duration: e.target.value})}
                />
              </div>
            )}
            <div className="edit-actions">
              <button data-testid="video-edit-cancel-button" onClick={() => setEditingItem(null)} className="btn-cancel">
                Cancelar
              </button>
              <button data-testid="video-edit-save-button" onClick={handleSaveEdit} className="btn-save">
                <Save size={18} />
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;